import {
  HeadwayVerificationStatus,
  LineOfBusiness,
  type Prisma,
} from "@prisma/client";

/**
 * Lines of business Marble never accepts in-network. Selecting one on a
 * client's plan is always treated as OON, regardless of payer.
 */
export const ALWAYS_OON_LINES_OF_BUSINESS: ReadonlySet<LineOfBusiness> =
  new Set([LineOfBusiness.MEDICARE]);

/**
 * Consolidated eligibility result string surfaced to humans (and Marty).
 * Computed by {@link getEligibilityCheckStatus} from the three parallel
 * verification status fields on `InsuranceVerification`.
 */
export type EligibilityCheckStatus =
  | "Eligible"
  | "Ineligible"
  | "Pending Vendor Check"
  | "Pending Manual Verification"
  | "Out of Network";

/**
 * The hydration include used by {@link getActiveInsuranceForStudent}. Mirrors
 * the shape consumed by the OON / eligibility helpers in
 * `@marble/core/modules/insurance/utils`.
 */
export const ACTIVE_INSURANCE_INCLUDE = {
  InsurancePayer: {
    include: {
      InsurancePayerNetworkStatus: {
        include: {
          insurancePayerPlans: true,
        },
      },
    },
  },
  InsurancePayerPlan: true,
  Address: true,
  InsuranceVerifications: {
    orderBy: { createdAt: "desc" },
    take: 1,
  },
} as const satisfies Prisma.InsuranceInfoInclude;

export type ActiveInsuranceForStudent = Prisma.InsuranceInfoGetPayload<{
  include: typeof ACTIVE_INSURANCE_INCLUDE;
}>;

export const INSURANCE_HISTORY_SELECT = {
  id: true,
  createdAt: true,
  activeStartDate: true,
  activeEndDate: true,
  newPlanCounter: true,
  lineOfBusiness: true,
  state: true,
  memberId: true,
  captureStatus: true,
  doesNotHaveInsurance: true,
  editSource: true,
  InsurancePayer: {
    select: {
      id: true,
      payerId: true,
      name: true,
    },
  },
  InsurancePayerPlan: {
    select: {
      id: true,
      name: true,
      isINN: true,
    },
  },
  InsuranceVerifications: {
    orderBy: { createdAt: "desc" },
    take: 1,
    select: {
      id: true,
      createdAt: true,
      nirvanaVerificationStatus: true,
      headwayVerificationStatus: true,
      manualVerificationStatus: true,
    },
  },
} as const satisfies Prisma.InsuranceInfoSelect;

export type InsuranceHistoryRow = Prisma.InsuranceInfoGetPayload<{
  select: typeof INSURANCE_HISTORY_SELECT;
}>;

/**
 * Headway statuses that mean the client is still being set up on Headway, so
 * we have not asked them to confirm their intake yet. Consumed by
 * {@link isConfirmationFlowSkippedForHeadwaySetup}.
 */
export const HEADWAY_SETUP_STATUSES_SKIPPING_CONFIRMATION: HeadwayVerificationStatus[] =
  [
    HeadwayVerificationStatus.AWAITING_CLIENT_HEADWAY_SETUP,
    HeadwayVerificationStatus.NEEDS_SUBMISSION_TO_HEADWAY,
  ];

// To avoid fat fingering payer ids
export const COMMON_PAYER_IDS = {
  AETNA: "60054",
  BCBS: "00803",
  CARELON: "CHCBH",
  CIGNA: "62308",
  EMBLEM: "55247",
  FIDELIS: "11315",
  HEALTHFIRST: "80141",
  NYS_MEDICAID: "141797357",
  METROPLUS: "13265",
  MOLINA_NY: "16146",
  MVP: "14165",
  OSCAR: "OSCAR",
  OXFORD: "06111",
  UNITED: "87726",
  CARESOURCE_OH: "0003150",
  BUCKEYE: "0004202",
  HUMANA_OH: "61103",
  AMERIHEALTH_OH: "84243",
  MOLINA_OH_MEDICAID: "73160",
  HORIZON_NJ: "22326",
  AETNA_BETTER_HEALTH_OF_NJ: "46320",
  WELLCARE_NJ: "14163",
  // Arizona Medicaid FFS (AHCCCS) / AIHP — "Medicaid Of Arizona".
  // LMSWs cannot bill these plans; scheduling is LCSW-only.
  AHCCCS_AZ: "866004791",
};

// Payers where approving a provider's Medicaid in-network (credentialing) toggle
// should automatically flip their Commercial side to in-network as well. These
// payers credential providers across both lines of business together, so keeping
// the toggles linked avoids manual drift between the two. See PE-1345.
export const PAYER_IDS_AUTO_INN_COMMERCIAL_WITH_MEDICAID = [
  // Healthfirst
  COMMON_PAYER_IDS.HEALTHFIRST,
  // Fidelis Care
  COMMON_PAYER_IDS.FIDELIS,
  // MetroPlus
  COMMON_PAYER_IDS.METROPLUS,
];
